import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  RefreshCw,
  Database,
  KeyRound,
  CreditCard,
  Webhook,
  Mail,
  ShieldCheck,
  Server,
  UserRound,
} from 'lucide-react';
import AdminNav from './AdminNav';
import AdminGate from './AdminGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECK_META = {
  authentication: { icon: UserRound, label: 'Authentication' },
  google_auth: { icon: KeyRound, label: 'Google Auth' },
  stripe: { icon: CreditCard, label: 'Stripe connectivity' },
  stripe_webhook: { icon: Webhook, label: 'Webhook verification' },
  email: { icon: Mail, label: 'Email service' },
  email_domain: { icon: ShieldCheck, label: 'Domain verification (DKIM/SPF)' },
  database: { icon: Database, label: 'Database' },
  deployment: { icon: Server, label: 'Deployment' },
};

const STATUS_STYLES = {
  ok: {
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    Icon: CheckCircle2,
    label: 'Healthy',
  },
  warn: {
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    Icon: AlertTriangle,
    label: 'Warning',
  },
  error: {
    dot: 'bg-red-500',
    text: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    Icon: XCircle,
    label: 'Error',
  },
};

const StatusPill = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.error;
  const { Icon } = style;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text} border ${style.border}`}
    >
      <Icon size={13} />
      {style.label}
    </span>
  );
};

const CheckRow = ({ id, status, detail }) => {
  const meta = CHECK_META[id] || { icon: Activity, label: id };
  const { icon: MetaIcon } = meta;
  return (
    <div
      data-testid={`status-row-${id}`}
      className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-b-0"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 flex-shrink-0">
          <MetaIcon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
          <p className="text-xs text-gray-500 mt-0.5 break-all">{detail}</p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <StatusPill status={status} />
      </div>
    </div>
  );
};

const AdminStatusInner = ({ adminEmail }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/system-status`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load system status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const overall = data?.overall;
  const overallStyle = overall ? STATUS_STYLES[overall] : null;
  const deployment = data?.checks?.deployment || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav activeEmail={adminEmail} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">
              <Activity size={13} /> System status
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Platform health</h1>
            {data?.generated_at && (
              <p className="text-xs text-gray-500 mt-1">
                Last updated {new Date(data.generated_at).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {overall && overallStyle && (
              <div
                data-testid="admin-status-overall"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${overallStyle.bg} ${overallStyle.text} border ${overallStyle.border}`}
              >
                <span className={`w-2 h-2 rounded-full ${overallStyle.dot}`} />
                <span className="text-sm font-bold uppercase tracking-wide">{overallStyle.label}</span>
              </div>
            )}
            <button
              data-testid="admin-status-refresh-btn"
              onClick={fetchStatus}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh
            </button>
          </div>
        </div>

        {loading && !data && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
            <Loader2 className="animate-spin text-gray-400 mx-auto mb-3" size={26} />
            <p className="text-sm text-gray-500">Running health checks…</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm mb-6">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Deployment / build banner */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400 tracking-wider">Environment</p>
                <p
                  data-testid="admin-status-env"
                  className="text-sm font-bold text-gray-900 mt-0.5 capitalize"
                >
                  {deployment.environment || '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400 tracking-wider">Build</p>
                <p
                  data-testid="admin-status-build-sha"
                  className="text-sm font-mono font-bold text-gray-900 mt-0.5"
                >
                  {deployment.build_sha || '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400 tracking-wider">Server started</p>
                <p className="text-sm font-medium text-gray-700 mt-0.5">
                  {deployment.server_started_at
                    ? new Date(deployment.server_started_at).toLocaleString()
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase text-gray-400 tracking-wider">Signed in as</p>
                <p className="text-sm font-medium text-gray-700 mt-0.5 truncate">{adminEmail}</p>
              </div>
            </div>

            {/* Checks list */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-2 sm:p-5">
              {Object.entries(data.checks)
                .filter(([id]) => id !== 'deployment')
                .map(([id, c]) => (
                  <CheckRow key={id} id={id} status={c.status} detail={c.detail} />
                ))}
            </div>

            <p className="text-xs text-gray-400 text-center mt-6">
              This page is refreshed on demand. Health checks probe external services with short
              timeouts; a transient <span className="font-mono">warn</span> may not indicate an
              outage.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

const AdminStatus = () => (
  <AdminGate>
    {({ adminEmail }) => <AdminStatusInner adminEmail={adminEmail} />}
  </AdminGate>
);

export default AdminStatus;
